import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import makeStyles from "@mui/styles/makeStyles";
import QuestionPreview from "../QuestionPreview";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { mediaUploadFile, mediaUploadDeleteFile } from "../../../services/api";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import { parseJwtToken } from "../../../services/commons";

import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

import styles from "./styles";
import { useTranslation } from "react-i18next";
import uuid from "react-uuid";
import _ from "lodash";

const useStyles = makeStyles((props) => styles(props));

const AnswerMediaUpload = (props) => {
  const styles = useStyles();
  const { t } = useTranslation("surveyTaker");
  const [contentUploadError, setContentUploadError] = useState("");
  const [fileInfoArr, setFileInfoArr] = useState([]);
  const remainingSize = useMemo(
    () =>
      fileInfoArr.reduce((acc, fileData) => acc + parseFloat(fileData.size), 0),
    [fileInfoArr]
  );
  const [fileUploadProgress, setFileUploadProgress] = useState({});
  const { currentSurveyId } = useSelector((state) => state.survey);
  const maxFileSize = parseInt(props.fileSizeType) * 1048576;
  const questionId = props.questionId;
  const questionLabel = props.questionLabel;

  const getFileAcceptedTypes = () => {
    let checkType = props.checkedFileTypes;
    const imageTypes = ".gif,.png,.jpg,.jpeg,.svg,.tif";
    const videoType = ".mov,.mp4";
    const pdfType = ".pdf";
    const excelType = ".xls,.xlsx,.csv";
    const powerType = ".ppt,.pptx";
    const docType = ".docx,.txt";
    let finalAcceptValue = "";
    checkType?.map((item) => {
      const v = item.value;
      switch (v) {
        case "image":
          finalAcceptValue = finalAcceptValue.concat(imageTypes);
          break;
        case "video":
          finalAcceptValue = finalAcceptValue.concat(videoType);
          break;
        case "worddoc":
          finalAcceptValue = finalAcceptValue.concat(docType);
          break;
        case "pdf":
          finalAcceptValue = finalAcceptValue.concat(pdfType);
          break;
        case "powerpoint":
          finalAcceptValue = finalAcceptValue.concat(powerType);
          break;
        case "excelcsv":
          finalAcceptValue = finalAcceptValue.concat(excelType);
          break;
        default:
          break;
      }
    });
    const re = /(\w+)\.(\w+)/;
    return finalAcceptValue.replace(re, "$1,.$2");
  };

  useEffect(() => {
    if (contentUploadError) {
      setTimeout(() => {
        setContentUploadError("");
      }, 8000);
    }
  }, [contentUploadError]);
  const { getRootProps, getInputProps } = useDropzone({
    accept: getFileAcceptedTypes(),
    multiple: true,
    maxFiles: props.maxFile,
    maxSize: maxFileSize,
    onDrop: async (acceptedFiles, fileRejections) => {
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          switch (err.code) {
            case "file-invalid-type":
              setContentUploadError(err.message);
              break;
            case "file-too-large":
              setContentUploadError(
                t("surveyTaker:maxSizeLimitMessage") +
                  props.fileSizeType +
                  " MB"
              );
              break;
            case "too-many-files":
              setContentUploadError(
                t("surveyTaker:maxFileLimitMessage") + props.maxFile
              );
              break;
            default:
              break;
          }
        });
      });
      if (fileInfoArr.length > props.maxFile - 1) {
        setContentUploadError(
          t("surveyTaker:maxFileLimitMessage") + props.maxFile
        );
      } else if (remainingSize > props.fileSizeType) {
        setContentUploadError(
          t("surveyTaker:maxSizeLimitMessage") + props.fileSizeType + " MB"
        );
      } else {
        const updatedFileUploadProgess = { ...fileUploadProgress };
        const acceptedFileInfoArr = acceptedFiles.map((file) => {
          const id = uuid() + file.size;
          updatedFileUploadProgess[id] = "uploading";
          return {
            type: file.type,
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            id,
          };
        });
        setFileUploadProgress(updatedFileUploadProgess);
        const allFileInfoArr = [...fileInfoArr, ...acceptedFileInfoArr];
        setFileInfoArr(allFileInfoArr);
        await startFileUpload(acceptedFiles, acceptedFileInfoArr);
      }
    },
  });

  useEffect(() => {
    if (props.value?.answers && !_.isEqual(props.value?.answers, fileInfoArr)) {
      setFileInfoArr(props.value?.answers);
      let fileUploadProgressTemp = [];
      props.value.answers.forEach(
        (fileInfo) => (fileUploadProgressTemp[[fileInfo.id]] = "Success")
      );
      setFileUploadProgress(fileUploadProgressTemp);
    }
  }, [props.value]);

  const handlePropsChange = (fileInfo) => {
    if (Object.values(fileUploadProgress).every((item) => item === "Success")) {
      props.handleChange({
        questionType: props.type,
        answers: fileInfo,
      });
    }
  };

  const onFileClear = async (id) => {
    const fileInfoTemp = fileInfoArr.filter((file) => file.id !== id);
    setFileInfoArr(fileInfoTemp);
    handlePropsChange(fileInfoTemp);
    const fileNameToBeDeleted = fileInfoArr.find(
      (item) => item.id == id
    ).fileUploadName;
    await mediaUploadDeleteFile(
      currentSurveyId,
      questionLabel,
      fileNameToBeDeleted
    );
  };
  const startFileUpload = async (acceptedFiles, acceptedFileInfoArr) => {
    let fileUpName = "";
    const responseIdVal = uuid().substring(0, 15);
    const payload = parseJwtToken();
    const { userid = "" } = payload;
    const fileTotalNames = acceptedFiles.map(async (file, index) => {
      try {
        const uploadedContent = await mediaUploadFile(
          file,
          currentSurveyId,
          questionLabel,
          "mU" + questionLabel + "-" + responseIdVal + "-" + file.size,
          getUserName(userid)
        );
        fileUpName = uploadedContent[0]["Blob Filename"];
        acceptedFileInfoArr[index].fileUploadName = fileUpName;
        setFileUploadProgress((prevState) => ({
          ...prevState,
          [acceptedFileInfoArr[index].id]: "Success",
        }));
        handlePropsChange([...fileInfoArr, ...acceptedFileInfoArr]);
      } catch (error) {
        setFileUploadProgress((prevState) => ({
          ...prevState,
          [acceptedFileInfoArr[index].id]: "failed",
        }));
      }
      return fileTotalNames;
    });
  };

  const getUserName = (userId) => {
    if (userId === null) {
      return null;
    }
    if (userId.includes("@")) {
      userId = userId.split("@")[0];
    }
    if (userId.includes("\\")) {
      const index = userId.lastIndexOf("\\");
      userId = userId.substring(index + 1);
    }
    return userId;
  };

  const showFileName = () => {
    return (
      <div
        className={props.maxFile > 1 ? styles.testFile : styles.testFileSingle}
      >
        {fileInfoArr.map((item, index) => {
          const isFileUploading = fileUploadProgress[item.id];
          return (
            <div key={`${index}`} className={styles.showPreviewMedia}>
              <InsertDriveFileIcon
                className={
                  item.i
                    ? styles.photoLibraryIconProgress
                    : styles.photoLibraryIcon
                }
              />
              <div
                id={`content-${index}`}
                className={
                  isFileUploading != "Success"
                    ? styles.contentPlaceHolderMediaProgress
                    : styles.contentPlaceHolderMedia
                }
              >
                <Box
                  sx={{ width: "100%" }}
                  aria-label={
                    isFileUploading == "Success"
                      ? `${item.name}- upload complete and total size ${remainingSize} used`
                      : ""
                  }
                >
                  {isFileUploading == "uploading"
                    ? "Uploading " + item.name
                    : item.name}
                  <div
                    className={
                      isFileUploading == "failed"
                        ? styles.errorText
                        : styles.testSize
                    }
                  >
                    {isFileUploading == "failed" ? (
                      <>
                        <ErrorIcon fontSize="18px" />{" "}
                        {t("surveyTaker:uploadFailedMessage")}
                      </>
                    ) : (
                      item.size
                    )}
                  </div>
                  {isFileUploading == "uploading" ? (
                    <LinearProgress
                      aria-label={`Please wait. uploading ${item.name}`}
                    />
                  ) : (
                    <></>
                  )}
                  <div
                    className={
                      isFileUploading == "failed"
                        ? styles.errorTextWrong
                        : styles.testSize
                    }
                  >
                    {isFileUploading == "failed" ? (
                      <>{t("surveyTaker:somethingWrongMessage")}</>
                    ) : (
                      ""
                    )}
                  </div>
                </Box>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Close ${item.name}`}
                  className={styles.clearFields}
                  onClick={() => onFileClear(item.id)}
                  data-testid="clearStateButton"
                >
                  <CloseIcon size="medium" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const showFileUpload = () => {
    return (
      <div
        className={
          props.maxFile > 1
            ? `${styles.mediaContainer}`
            : `${styles.mediaContainerSingle}`
        }
      >
        <div
          className={
            contentUploadError
              ? `${styles.uploadContainerMediaFailure}`
              : `${styles.uploadContainerMedia}`
          }
        >
          <div
            className={styles.imageUpload}
            data-testid="dragBox"
            {...getRootProps()}
            onClick={(e) => e.stopPropagation()}
          >
            <label htmlFor={`fileInput-${questionId}`}>
              <CloudUploadIcon className={styles.photoLibraryIcon} />
              <input
                id={`fileInput-${questionId}`}
                type="file"
                {...getInputProps()}
                data-testid="fileInput"
              />
            </label>
            {contentUploadError ? (
              <p className={styles.uploadFailureMsg}>{contentUploadError}</p>
            ) : (
              <>
                <div className={styles.title}>
                  <p className={styles.dragAndDroptext}>
                    {t("surveyTaker:drag")}
                  </p>
                  <label htmlFor={`fileInput-${questionId}`}>
                    <p
                      role="definition"
                      aria-label="Browse Files to Upload"
                      className={styles.dragAndDroptext}
                    >
                      {t("surveyTaker:or")}
                      <span className={styles.browseFileText}>
                        {t("surveyTaker:browse")}
                      </span>
                    </p>
                    <input
                      id={`fileInputText-${questionId}`}
                      type="file"
                      {...getInputProps()}
                    />
                  </label>
                </div>
                {contentUploadError}
              </>
            )}
          </div>
        </div>
        <div
          id={`sizeDisplay-${questionId}`}
          className={`${styles.displaySize}`}
        >
          <p className={`${styles.displayText}`}>
            Format Accepted: {getFileAcceptedTypes()}
          </p>
          <p className={`${styles.displaySizeLeft}`}>
            {remainingSize} of {props.fileSizeType} MB
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.questionWrapper}>
      <QuestionPreview
        question={props.question}
        helperText={props.helperText}
        required={props.required}
      />
      <div className={`${styles.mediaAnswerDiv}`}>
        <>
          {props.maxFile == 1 && fileInfoArr.length === 0 ? (
            showFileUpload()
          ) : props.maxFile > 1 ? (
            <>
              <>{showFileUpload()}</>
              <> {showFileName()}</>{" "}
            </>
          ) : (
            showFileName()
          )}
        </>
      </div>
    </div>
  );
};

AnswerMediaUpload.propTypes = {
  question: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default AnswerMediaUpload;
